import { useContext, useEffect } from 'react'
import { unique, uniqueBy } from 'lib/array/arrayUtils'
import { fetchExperiencesById, fetchExperienceTimes } from 'actions/ExperienceActions'
import { getExperienceTimesKey } from 'lib/experiences/experienceUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getUserCartExperienceItems, getUserCartTransferItems } from 'selectors/userCart'
import GeoContext from 'contexts/geoContext'

function useFetchExperienceItemData() {
  const dispatch = useAppDispatch()

  const experienceItems = useAppSelector(getUserCartExperienceItems)
  const transferItems = useAppSelector(getUserCartTransferItems)
  const { currentCurrency } = useContext(GeoContext)

  useEffect(() => {
    const experienceFetchItems = experienceItems.filter(item => item.offerType !== 'addon')
    // Fetch experiences if needed
    const experienceIds = unique(experienceFetchItems.map(item => item.experienceId))
    dispatch(fetchExperiencesById(...experienceIds))

    // Get the ticket data for each combination of experience, date, and pickup point
    // so we can retrieve the tickets
    const itemsToFetchTimesFor = uniqueBy(experienceFetchItems, (item) =>
      getExperienceTimesKey(item.experienceId, item.date, {
        currency: currentCurrency,
        pickupPointId: item.pickupPointId,
        redemptionLocationId: item.redemptionLocationId,
        ticketMode: item.ticketModeKey,
      }),
    )
    const experienceTimesParams = itemsToFetchTimesFor.map(
      (item: App.Checkout.ExperienceItem): Parameters<typeof fetchExperienceTimes> => [
        item.experienceId,
        {
          date: item.date,
          currency: currentCurrency,
          pickupPointId: item.pickupPointId,
          redemptionLocationId: item.redemptionLocationId,
          isBuyNowBookLater: item.isBuyNowBookLater,
          ticketModeKey: item.ticketModeKey,
          isGift: item.isGift,
        },
      ],
    )

    for (const params of experienceTimesParams) {
      dispatch(fetchExperienceTimes(...params))
    }
  }, [currentCurrency, dispatch, experienceItems])

  useEffect(() => {
    dispatch(fetchExperiencesById(...transferItems.map(transfer => transfer.experienceId)))
  }, [dispatch, transferItems])
}

export default useFetchExperienceItemData
